<template>
  <div class="ca-renew">
    <div class="ca-renew__header">
      <h2 class="ca-renew__title">CA在线续签</h2>
    </div>

    <!-- 仅保留制证段模板：身份核验未通过时隐藏「确定续签」，改为「发起身份核验」 -->
    <div v-loading="pageLoading" class="ca-renew__card">
      <div class="ca-renew__fields">
        <div class="ca-renew__field">
          <span class="ca-renew__label"><em>*</em>CA使用者</span>
          <el-input :value="caUser" disabled class="ca-renew__input" />
        </div>
        <div class="ca-renew__field">
          <span class="ca-renew__label"><em>*</em>CA有效期</span>
          <el-input :value="caExpire" disabled class="ca-renew__input" />
        </div>
        <div class="ca-renew__field">
          <span class="ca-renew__label"><em>*</em>续签年限</span>
          <!-- 只读展示申请页选择的年限（订单详情 limitName），初始化请求订单详情接口带出 -->
          <el-input :value="limitName" disabled class="ca-renew__input" />
        </div>
      </div>

      <div class="ca-renew__notices">
        <p v-if="envReason" class="ca-renew__notice ca-renew__notice--error">
          <b>环境提示</b>{{ envReason }}请使用 Windows 系统 IE 浏览器（或 360/搜狗等 IE 兼容模式）打开本页。
        </p>
        <p v-if="widgetPackageFileId" class="ca-renew__notice">
          <b>注意</b>首次续费时，先点击【下载控件】，解压CFCA控件压缩包，安装对应控件（如电脑已安装过相应控件，可不用重复安装）。
        </p>
        <p v-if="widgetPackageFileId" class="ca-renew__notice">
          <b>注意</b>CFCA控件压缩包中提供了CFCA在线续费操作说明，可按照操作说明进行操作。
        </p>
        <p class="ca-renew__notice">
          <b>注意</b>点击【发起身份核验】，实名认证通过之后才能进行续费。
        </p>
        <p class="ca-renew__notice">
          <b>注意</b>CFCA在线续费时，只能插入一张CFCA，请勿插入多张，若因插入多张CA导致续费失败。需重新办理CA，且续费产生的费用不予退订。
        </p>
        <!-- 需要重新认证的原因：仅订单已认证通过（40）时展示，表示本次是重新认证 -->
        <p
          v-if="authStatusDesc && orderStatus === '40'"
          class="ca-renew__notice ca-renew__notice--error"
        >
          <b>重新发起身份核验原因</b>{{ authStatusDesc }}
        </p>
      </div>

      <div class="ca-renew__actions">
        <!-- 控件压缩包 id 由订单详情下发，无数据时不展示下载按钮 -->
        <el-button
          v-if="widgetPackageFileId"
          type="danger"
          icon="el-icon-download"
          :loading="downloadingControl"
          @click="handleDownloadControl"
        >
          下载控件
        </el-button>
        <!-- 初始化已自动尝试过读取：成功（已插 UKEY）则按钮不显示；失败（未插入）保留按钮手动再取 -->
        <el-button
          v-if="!caInfoReady && !envReason && !pendingAuth"
          type="danger"
          icon="el-icon-search"
          :disabled="!envOk"
          :loading="readingCa"
          @click="handleFetchCaInfo()"
        >
          获取CA信息
        </el-button>
        <!-- 身份核验未通过：隐藏确定续签，改为发起身份核验；核验通过后恢复确定续签 -->
        <el-button
          v-if="pendingAuth"
          type="danger"
          icon="el-icon-key"
          :loading="authStarting"
          @click="handleAuthStart"
        >
          发起身份核验
        </el-button>
        <el-button
          v-else
          type="danger"
          icon="el-icon-check"
          :disabled="!envOk"
          :loading="renewing"
          @click="handleRenew"
        >
          确定续签
        </el-button>
      </div>
    </div>

    <!-- 手工人脸核验场景：移动端认证无回调，需用户回页点击「已完成身份核验」查询结果 -->
    <auth-result-dialog ref="authResultDialog" @success="handleAuthVerified" />
  </div>
</template>

<script>
import {
  getOrderDetail,
  authStartCaRenew,
  submitCaOnline,
  ra5102,
  updateRenewOrder,
} from '@/api/ca'
import {
  checkEnv,
  selectSignCert,
  getSignCertInfo,
  getLastErrorDesc,
  checkCertEnrollment,
  generateP10,
  importCerts,
} from '../util/cfcaRenewKit'
import { download } from '@/shared/file'
import AuthResultDialog from '../components/AuthResultDialog.vue'

export default {
  name: 'CaOrderRenew',
  components: { AuthResultDialog },
  data() {
    return {
      // 订单状态（数字码字符串，文档 §11.0）：30/80 待认证，40 认证通过（制证段），其余状态兜底回列表
      orderStatus: '',
      // 身份核验状态（authStart 返回 status）：'1' 已核验通过可制证；0 未激活 / 5 人工审核中需再次核验
      authStatus: '',
      // authStart 是否已成功返回：false（接口异常）时按钮显隐回退按订单状态判断
      authResolved: false,
      // authStart 返回的认证上下文：初始化先保存，点「发起身份核验」时直接复用，不重复请求
      authUrl: '',
      authExpiryTime: '',
      authStatusDesc: '',
      // 「发起身份核验」按钮 loading
      authStarting: false,
      // 制证段是否已初始化（环境检测 + 静默读取 CA 信息只做一次）
      certInited: false,
      // 两个只读字段：CA 使用者 / 有效期由 CFCA ukey 控件读取（点「获取CA信息」触发），未读取前展示 '--'
      caUser: '--',
      caExpire: '--',
      limitName: '--', // 续签年限：订单详情接口带出（申请页选择的年限），未加载前展示 '--'
      orderLimit: '', // 订单详情返回的购买年限描述，确定续签时作为 limit 原样上送（文档 §11.2 必填）
      // 运行环境：制证段仅支持 Windows + IE（ActiveX 控件方案），不满足时页内提示 + 禁用按钮
      envOk: true,
      envReason: '',
      // 控件读取的证书上下文（续签时随接口提交）
      subjectDN: '',
      startDate: '',
      endDate: '',
      // 订单详情带出的续签上下文（等价老系统 FreeMarker 注入的 remainNumber/remainLegalNumber）
      remainNumber: 0,
      remainLegalNumber: 0,
      // 续签中间态：CFCA RA 5106 返回的序列号/授权码
      // 上次续签在换凭证后中断时后端已持久化，重试直接复用跳过 submit，避免重复调 5106；
      serialNo: '',
      authCode: '',
      // 控件压缩包文件 id（订单详情下发），「下载控件」走通用文件下载接口
      widgetPackageFileId: '',
      // 按钮 loading 锁
      readingCa: false,
      renewing: false,
      // 「下载控件」loading：安装包较大时下载耗时较长，按钮显示加载态避免用户重复点击
      downloadingControl: false,
      // 页面初始化 loading（订单详情完成前遮盖卡片）
      pageLoading: false,
      // CA 信息是否已读取成功（成功则隐藏「获取CA信息」按钮）
      caInfoReady: false,
    }
  },
  computed: {
    // 订单 id：从订单卡片「CA在线续费」（CFCA 续费订单）跳转带入；上海CA 续期走 sheca/renew 取 authUrl，不进本页
    orderId() {
      return this.$route.query.orderId
    },
    /**
     * 是否仍需身份核验：核验结果已拿到时以 authStart 状态为准（status=1 才可制证），
     * 接口异常（未拿到结果）时回退按订单状态判断（30 待认证 / 80 已驳回）。
     * 需核验时隐藏「确定续签」，展示「发起身份核验」。
     */
    pendingAuth() {
      if (this.authResolved) return this.authStatus !== '1'
      return this.orderStatus === '30' || this.orderStatus === '80'
    },
    // 订单状态是否支持在线续签（30 待认证 / 40 认证通过 / 80 已驳回），其余状态兜底回列表
    supportedOrder() {
      return ['30', '40', '80'].indexOf(this.orderStatus) >= 0
    },
  },
  created() {
    // 订单 id 缺失兜底：续签从订单卡片进入必带 orderId；直接访问缺参时不发请求，提示后回列表
    if (!this.orderId) {
      this.$message.warning('订单参数缺失，已返回订单列表')
      this.$router.replace({ name: 'ca-orders' })
      return
    }
    this.pageLoading = true
    this.initPage().finally(() => {
      this.pageLoading = false
    })
  },
  methods: {
    /**
     * 页面初始化：先拉订单详情（状态 / 续签年限 / 续签上下文），
     * 再调 authStart 预检身份核验状态——核验通过后仍可能因凭证超上限 / 超有效期需要重新核验，
     * 因此初始化就要查询，而不是等到点「发起身份核验」才查。
     */
    async initPage() {
      const loaded = await this.fetchOrderDetail()
      if (!loaded) return
      if (!this.supportedOrder) {
        this.$alert('订单当前状态不支持在线续签', '提示', {
          type: 'warning',
          callback: () => this.$router.replace({ name: 'ca-orders' }),
        })
        return
      }
      const needAuth = await this.loadAuthInfo()
      if (!needAuth) {
        // 核验已通过：重新同步订单状态（核验回调可能已驱动流转 40）后进入制证段
        await this.fetchOrderDetail()
        this.initCertPhase()
      }
      // 初始化返回 0（需核验）：只保存认证上下文，不自动打开认证页、不弹确认框，
      // 等用户点「发起身份核验」时再打开认证页 + 弹框（有描述则一并展示）
    },
    /**
     * 拉取订单详情：带出订单状态、续签年限（limitName）、续签上下文（剩余可续签数量 / 暂存凭证 / 控件包）。
     * @returns {Promise<boolean>} 是否成功取到详情
     */
    fetchOrderDetail() {
      return getOrderDetail({ orderId: this.orderId })
        .then((res) => {
          this.applyDetail(res.data || {})
          return true
        })
        .catch(() => {
          // 业务报错已由 http 拦截器 toast
          return false
        })
    },
    // 订单详情字段落位
    applyDetail(info) {
      this.orderStatus = String(info.orderStatus || '')
      this.limitName = info.limitName || '--'
      this.orderLimit = info.orderLimit || ''
      this.remainNumber = Number(info.remainNumber) || 0
      this.remainLegalNumber = Number(info.remainLegalNumber) || 0
      this.widgetPackageFileId = info.widgetPackageFileId || ''
      // 续签中间态：上次中断留下的 serialNo/authCode，重试时复用（见 handleRenew 步骤①）
      this.serialNo = info.serialNo || ''
      this.authCode = info.authCode || ''
    },
    /**
     * 制证段初始化（只执行一次）：续签制证仅支持 Windows + IE（ActiveX 控件方案），
     * 不满足时页内红字提示 + 禁用按钮；随后静默尝试读取 CA 信息——已插入 UKEY 直接带出并隐藏
     * 「获取CA信息」按钮，未插入则静默失败，插入后手动点击再取（内部已 catch，不会 reject）。
     */
    initCertPhase() {
      if (this.certInited) return
      this.certInited = true
      const env = checkEnv()
      if (!env.ok) {
        this.envOk = false
        this.envReason = env.reason
        return
      }
      this.handleFetchCaInfo(true)
    },
    /**
     * 查询身份核验状态（文档 §11.1 authStart）：后端先 5121 预检，未失效流水号直接复用不重复发短信，
     * 失效 / 已使用 / 人工拒绝才重新发起 5122。初始化时调用，结果存下来供「发起身份核验」直接复用。
     * @returns {Promise<boolean>} 是否仍需身份核验
     */
    async loadAuthInfo() {
      try {
        const res = await authStartCaRenew({ orderId: this.orderId })
        this.applyAuthInfo(res.data || {})
      } catch (e) {
        // 业务报错已由 http 拦截器 toast（如「订单缺少经办人手机号」），按钮显隐回退按订单状态
        this.authResolved = false
      }
      return this.pendingAuth
    },
    // authStart 结果落位：认证链接 / 有效期 / 状态描述先保存，status=1 表示可继续制证
    applyAuthInfo(data) {
      this.authUrl = data.authUrl || ''
      this.authExpiryTime = data.authExpiryTime || ''
      this.authStatus = data.status === null || data.status === undefined ? '' : String(data.status)
      // 核验已通过时不再展示状态描述（那是需要重新认证的原因）
      this.authStatusDesc = this.authStatus === '1' ? '' : data.statusDesc || ''
      this.authResolved = true
    },
    /**
     * 发起身份核验：直接用初始化查询到的认证链接打开认证页 + 弹框引导用户完成后手工确认
     * （移动端认证无回调）。若初始化未拿到认证链接（接口异常），此处补查一次。
     */
    // 已有认证链接：直接复用打开认证页（不重复请求，避免重发短信）
    handleAuthStart() {
      if (this.authStarting) return
      if (this.authUrl) {
        this.enterAuthFlow()
        return
      }
      // 初始化未拿到认证链接（接口异常）：此处补查一次
      this.authStarting = true
      this.loadAuthInfo()
        .then((needAuth) => {
          // 补查后发现已核验通过：直接进入续签流程，不再打开认证链接
          if (needAuth) {
            this.enterAuthFlow()
          } else {
            this.handleAuthVerified()
          }
        })
        .finally(() => {
          this.authStarting = false
        })
    },
    // 进入核验交互：按场景决定是否打开认证页 + 弹出确认完成弹框
    enterAuthFlow() {
      // 需要打开认证页的两种场景：
      // ① 首次核验：status=0（未激活）且无描述，用户点按钮直接进入认证页；
      // ② 已认证通过（40）后重新核验：有重新认证原因（凭证超上限 / 超有效期等）。
      // 其余情况（如 status=5 人工审核中仅等待结果）不重复打开认证页，只弹框引导确认。
      const needOpenAuthPage =
        (this.authStatus === '0' && !this.authStatusDesc) ||
        (this.authStatusDesc && this.orderStatus === '40')
      if (needOpenAuthPage) {
        this.openAuthUrl()
      }
      this.openAuthResultDialog()
    },
    // 打开核验确认弹框（内部再点「已完成身份核验」会调接口查询结果）
    openAuthResultDialog() {
      this.$refs.authResultDialog.open({
        orderId: this.orderId,
        authUrl: this.authUrl,
        expiryTime: this.authExpiryTime,
      })
    },
    // 打开 CFCA 认证页面（短信验证码 + 人脸等核验在 CFCA 侧完成）
    openAuthUrl() {
      if (this.authUrl) {
        window.open(this.authUrl, '_blank')
      }
    },
    /**
     * 身份核验通过（弹框内「已完成身份核验」返回 status=1）：
     * 重新拉取订单详情，订单流转 40 后初始化制证段（环境检测 + 静默读 CA 信息）。
     */
    handleAuthVerified() {
      this.authStatus = '1'
      this.authResolved = true
      // 核验已通过：清空上次的重新认证原因，避免刷新后误展示
      this.authStatusDesc = ''
      this.$message.success('身份核验已完成，请插入CA后继续在线续签')
      this.fetchOrderDetail().then(() => {
        this.initCertPhase()
      })
    },
    /**
     * 下载控件：控件压缩包文件 id 由订单详情接口下发（widgetPackageFileId），
     * 走通用文件下载方法 download(id)，先查文件名再以 blob 下载，
     * 避免浏览器把返回的 Content-Disposition 文件名忽略、最终落盘为文件 id。
     * 安装成功后再点「确定续签」。
     */
    async handleDownloadControl() {
      if (!this.widgetPackageFileId) {
        this.$message.warning('控件安装包信息未获取到，请刷新页面重试')
        return
      }
      if (this.downloadingControl) return
      // 安装包较大、下载接口耗时较长：期间按钮显示 loading，防止重复点击重复下载
      this.downloadingControl = true
      try {
        await download(this.widgetPackageFileId)
      } finally {
        this.downloadingControl = false
      }
    },
    /**
     * 获取CA信息：选证书 → 读 SubjectCN（按 @ 分割取第二段为使用者）与起止日期。
     * 照搬老系统 onLine.js：任何一步异常视为未插 CA / 未装驱动。
     * @param {boolean} [silent] 为 true 时（制证段初始化自动尝试）失败不弹错误提示
     */
    async handleFetchCaInfo(silent) {
      if (this.readingCa) return
      this.readingCa = true
      try {
        await selectSignCert()
        const [subjectCN, subjectDN, timeFrom, timeOut] = await Promise.all([
          getSignCertInfo('SubjectCN'),
          getSignCertInfo('SubjectDN'),
          getSignCertInfo('CertTimeFrom'),
          getSignCertInfo('CertTimeOut'),
        ])
        // 机构名称：老系统取 SubjectCN 按 @ 分割的第二段
        const parts = String(subjectCN || '').split('@')
        this.caUser = parts[1] || subjectCN || '--'
        this.subjectDN = subjectDN || ''
        this.startDate = String(timeFrom || '').substr(0, 10)
        this.endDate = String(timeOut || '').substr(0, 10)
        this.caExpire = this.startDate && this.endDate ? this.startDate + '-' + this.endDate : '--'
        // 读取成功：CA 信息已带出，隐藏「获取CA信息」按钮
        this.caInfoReady = true
      } catch (e) {
        if (!silent) {
          const desc = getLastErrorDesc()
          this.$message.error(desc || '请先插入CA！')
        }
      } finally {
        this.readingCa = false
      }
    },
    /**
     * 确定续签：照搬老系统 onLine.html submit() 链（文档 §11.5 ④~⑨）——
     * ① submitCaOnline 换 serialNo/authCode（订单已有中间态则复用跳过）→ ② 控件生成 P10 →
     * ③ ra5102 换发证书 → ④ 控件把证书灌回 UKEY → ⑤ updateRenewOrder 回写订单。
     * 前置：CertEnrollment 探活（未安装提示）、必须已读取 CA 信息、按证书类型校验剩余可续签数量。
     */
    async handleRenew() {
      if (this.renewing) return
      try {
        await checkCertEnrollment()
      } catch (e) {
        this.$alert('控件未安装，请先点击「下载控件」安装成功后重试。', '提示', { type: 'warning' })
        return
      }
      if (!this.startDate) {
        this.$alert('请先确认CA已插入，并点击「获取CA信息」读取证书信息！', '提示', {
          type: 'warning',
        })
        return
      }
      // 证书类型：SubjectDN 含 OU=Individual 为个人/法人证书，OU=Organizational 为企业证书
      let certType = ''
      if (this.subjectDN.indexOf('OU=Individual') >= 0) {
        if (this.remainLegalNumber <= 0) {
          this.$message.error('可续签法人证书剩余数量小于1，请确认CA插入无误')
          return
        }
        certType = '1'
      } else if (this.subjectDN.indexOf('OU=Organizational') >= 0) {
        if (this.remainNumber <= 0) {
          this.$message.error('可续签CFCA剩余数量小于1，请确认CA插入无误')
          return
        }
        certType = '2'
      }
      this.renewing = true
      try {
        // ① 提交 DN + certType 换序列号 + 授权码（文档 §11.2；日期按 yyyyMMdd 上送；
        // limit 必填：订单详情返回的 orderLimit 字符串原样上送）。
        // 订单已有续签中间态（上次中断后端持久化的 serialNo/authCode）则复用跳过本步，避免重复调 5106
        let dn = this.subjectDN
        if (!this.serialNo || !this.authCode) {
          const submitRes = await submitCaOnline({
            orderId: this.orderId,
            dn: this.subjectDN,
            certType,
            limit: this.orderLimit,
            startDate: this.startDate.replace(/\D/g, ''),
            endDate: this.endDate.replace(/\D/g, ''),
          })
          const submitData = submitRes.data || {}
          this.serialNo = submitData.serialNo || ''
          this.authCode = submitData.authCode || ''
          dn = submitData.dn || dn
        }
        // ② 控件本地生成 P10（密钥留在 UKEY，只出证书请求）
        const { p10, containerName } = await generateP10(dn)
        // ③ P10 换发证书（文档 §11.3；RSA 单证书场景无需 p10Sub）
        const raRes = await ra5102({
          serialNo: this.serialNo,
          authCode: this.authCode,
          p10,
        })
        const { signatureCert, encryptionCert, encryptionPrivateKey } = raRes.data || {}
        // ④ 控件把换发的签名证书 + 加密证书对灌回 UKEY
        await importCerts({ signatureCert, encryptionCert, encryptionPrivateKey, containerName })
        // ⑤ 回写订单（文档 §11.4：仅 orderId + certType，剩余数量 -1，扣尽订单→60 已完成）
        await updateRenewOrder({
          orderId: this.orderId,
          certType,
        })
        this.$alert('操作成功', '提示', {
          type: 'success',
          callback: () => this.$router.replace({ name: 'ca-orders' }),
        })
      } catch (e) {
        if (e && e.message) {
          // this.$message.error('续签失败：' + e.message)
        }
      } finally {
        this.renewing = false
      }
    },
  },
}
</script>
